import React, { useEffect, useState } from "react";
import EcoIcon from "./ecoIcon";
import { ECO_TINTS } from "./ecosystemData";

// ---------- shared body content ----------
const DetailHeader = ({ service, onClose }) => (
    <div className="flex items-start justify-between gap-5 mb-9 lg:mb-12">
        <div className="max-w-2xl">
            <h3 className="font-bold text-2xl lg:text-[28px] tracking-tight text-blue10 leading-tight m-0">
                {service.title}
            </h3>
            <p className="text-sm lg:text-[15px] leading-relaxed text-blue70 mt-3 mb-0 max-w-xl">
                {service.longTagline}
            </p>
        </div>
        <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-full bg-white10 hover:bg-FGNeutralAccent transition-colors grid place-items-center text-blue10"
        >
            <EcoIcon name="close" size={15} color="#0F1424" />
        </button>
    </div>
);

// The graphite (Design & Dev) ink reads as an off-brand grey on a solid
// fill — fall back to the brand navy so every blue accent stays on-palette.
const ctaColor = (service, tint) => (service.id === "fdd" ? "#182553" : tint.ink);

const SectionLabel = ({ children, tint }) => (
    <div
        className="text-[10.5px] font-bold uppercase tracking-[0.22em] font-body mb-4"
        style={{ color: tint.ink }}
    >
        {children}
    </div>
);

// List item box. Resting: white with a hairline border. On hover it runs the
// same wash as the CTA — solid navy base + the btnHover blue radial — with the
// label fading to white. No arrow.
const ListBox = ({ children, ctaBg }) => (
    <div className="group/box relative overflow-hidden px-4 py-3.5 rounded-lg bg-white border border-solid border-FGNeutralAccent hover:border-transparent transition-[border-color] duration-500 ease-in-out cursor-default">
        <span
            aria-hidden="true"
            className="absolute -inset-px opacity-0 group-hover/box:opacity-100 transition-opacity duration-500 ease-in-out bg-btnHover"
            style={{ backgroundColor: ctaBg }}
        />
        <span className="relative z-10 block text-[13.5px] text-blue70 leading-snug transition-colors duration-500 ease-in-out group-hover/box:text-white">
            {children}
        </span>
    </div>
);

const DetailBody = ({ service, tint }) => {
    const ctaBg = ctaColor(service, tint);
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-8 lg:gap-x-12">
            {/* LEFT: what's included */}
            <div>
                <SectionLabel tint={tint}>What's included</SectionLabel>
                <div className="flex flex-col gap-3.5">
                    {service.bullets.map((b) => (
                        <ListBox key={b} ctaBg={ctaBg}>{b}</ListBox>
                    ))}
                </div>
            </div>

            {/* RIGHT: best for — divider keeps it aligned to the left column */}
            <div className="lg:border-l lg:border-solid lg:border-FGNeutralAccent lg:pl-12">
                <SectionLabel tint={tint}>Best for</SectionLabel>
                <div className="flex flex-col gap-3.5">
                    {service.forWho.map((b) => (
                        <ListBox key={b} ctaBg={ctaBg}>{b}</ListBox>
                    ))}
                </div>
            </div>
        </div>
    );
};

const DetailFooter = ({ service, tint }) => {
    const ctaBg = ctaColor(service, tint);
    return (
      <div className="mt-8 lg:mt-10 pt-6 border-t border-solid border-FGNeutralAccent flex flex-col sm:flex-row sm:justify-between sm:items-center gap-5">
        <span className="text-[15px] lg:text-base text-blue70 leading-snug max-w-xs">
          Not sure if this is the right fit?
        </span>
        {/* CTA mirrors the header button: text shifts left, arrow fades in, gradient washes up */}
        <a
          href={"https://app.usemotion.com/meet/PMG/meeting"}
          target="_blank"
          rel="noreferrer"
          className="btn group relative overflow-hidden h-14 px-10 rounded-full text-white text-base font-medium inline-flex items-center justify-center gap-3 transition-all duration-500 ease-in-out shrink-0"
          style={{ background: ctaBg }}
        >
          <div className="z-0 absolute top-0 left-0 w-full h-full rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-in-out bg-btnHover" />
          <span className="z-10">Talk to us</span>
          <span className="icon-hover z-10">
            <EcoIcon name="arrow" size={18} color="#fff" />
          </span>
        </a>
      </div>
    );
};

// ---------- the drawer ----------
// Sits between the card grid and the next section. Animated height collapse
// (grid-template-rows trick) + opacity + translate. Caret aligns to the
// active card on lg (3-col layout). Renders persistently so closing animates
// rather than unmounting instantly; renderSvc retains the last service so
// content doesn't blank during the collapse.
const ServiceInlineDrawer = ({ service, activeIndex, onClose }) => {
    const open = !!service;
    const [renderSvc, setRenderSvc] = useState(service);
    const [renderIdx, setRenderIdx] = useState(activeIndex);

    useEffect(() => {
        if (service) {
            setRenderSvc(service);
            setRenderIdx(activeIndex);
        }
    }, [service, activeIndex]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && open) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const tint = renderSvc ? ECO_TINTS[renderSvc.tintKey] : ECO_TINTS.navy;
    // 3-col grid centers: 1/6, 1/2, 5/6
    const caretPct = renderIdx === 0 ? "16.6%" : renderIdx === 1 ? "50%" : "83.4%";

    return (
        <div
            style={{
                display: "grid",
                gridTemplateRows: open ? "1fr" : "0fr",
                transition: "grid-template-rows .5s cubic-bezier(.2,.7,.2,1)",
                marginTop: open ? 20 : 0,
            }}
        >
            <div style={{ overflow: "hidden" }}>
                <div
                    className="relative bg-white border border-solid border-FGNeutralAccent rounded-2xl p-6 lg:p-10"
                    style={{
                        boxShadow: "0 1px 2px rgba(15,20,36,0.04), 0 16px 36px rgba(15,20,36,0.08)",
                        transform: open ? "translateY(0)" : "translateY(-12px)",
                        opacity: open ? 1 : 0,
                        transition: "opacity .35s ease, transform .35s ease",
                    }}
                >
                    <style>{`
                        @keyframes sdvFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
                    `}</style>

                    {/* tinted top accent rail */}
                    <div
                        className="absolute left-6 right-6 lg:left-10 lg:right-10 h-[3px] rounded-full"
                        style={{ top: 0, background: tint.ink, opacity: 0.85 }}
                    />

                    {/* caret pointer aligned to active card (lg only — predictable 3-col) */}
                    <div
                        className="hidden lg:block absolute"
                        style={{
                            top: -9,
                            left: caretPct,
                            transform: "translateX(-50%) rotate(45deg)",
                            width: 16,
                            height: 16,
                            background: "#fff",
                            borderTop: "1px solid #DEDFE0",
                            borderLeft: "1px solid #DEDFE0",
                            transition: "left .45s cubic-bezier(.2,.7,.2,1)",
                        }}
                    />

                    {renderSvc && (
                        <div key={renderSvc.id} style={{ animation: "sdvFade .35s ease both" }}>
                            <DetailHeader service={renderSvc} onClose={onClose} />
                            <DetailBody service={renderSvc} tint={tint} />
                            <DetailFooter service={renderSvc} tint={tint} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ServiceInlineDrawer;
